import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import { NavController } from 'ionic-angular';
import { AppConfig } from '../../../../app/app.config';
import { JackingV2Service } from '../../../../services/jacking-v2/jacking-v2.service';
import { DateTime } from 'luxon';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { JackingActivityV2EditPage } from '../jacking-activity-edit-v2/jacking-activity-edit-v2';
import { ValidationResult } from '../../../../models/projects/validation.models';
import { CompleteDayFilters } from '../../../../models/complete-day.models';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import {fieldPositioningColumns} from '../../v1/jacking-activity-list-component/field-positioning-list-component.config';
import {JackingPermissions} from '../../../../models/permissions/permission.models';
import {State} from '@progress/kendo-data-query';
import {FieldPositioningEditor} from '../../../../models/field-positioning/field-positioning.models';
import {GridDataResult} from '@progress/kendo-angular-grid';
import {FieldPositioningService} from '../../../../services/field-positioning/field-positioning.service';
import {PermissionsService} from '../../../../services/permissions/permissions.service';
import {DateService} from '../../../../services/date/date.service';
import {JackingActivityReportService} from '../../../../services/jacking-activity/jacking-activity-report.service';
import { process } from '@progress/kendo-data-query';
import { HttpResponse } from '@angular/common/http';
import {FieldPositioningEditPage} from '../../../field-positioning/field-positioning-edit/field-positioning-edit';
import {OnSiteMovementEditor} from '../../../../models/on-site-movement/on-site-movement.models';
import { onSiteMovementColumns } from '../../v1/jacking-activity-list-component/on-site-movement-list-component.config';
import {OnSiteMovementService} from '../../../../services/on-site-movement/on-site-movement.service';
import {OnSiteMovementEditPage} from '../../../on-site-movement/on-site-movement-edit/on-site-movement-edit';
import {JackingFormInstanceSummary} from '../../../../models/jacking-v2/jacking.models';
import {finalize} from 'rxjs/operators';
import {JackingV2FileDownloadService} from '../../../../services/jacking-v2/jacking-v2-file-download.service';
import {ValidationService} from '../../../../services/validation/validation.service';
import {ImplementerService} from '../../../../services/implementer/implementer.service';
import {cloneDeep} from 'lodash';
import {DataChangeNotification, SignalRService} from '../../../../services/signalR/signalRService';
import {VesselContextService} from '../../../../services/vessel/vessel-context.service';
import {Subscription} from 'rxjs/Subscription';

@Component({
  selector: 'app-jacking-activity-list-v2',
  templateUrl: './jacking-activity-list-v2.component.html',
})
export class JackingActivityListV2Component implements OnDestroy {
  @Input() validationResult: ValidationResult = new ValidationResult();
  @Input() validationLoading = false;
  @Input() loading: boolean = false;

  @Output() uiChange = new EventEmitter<{
    validationResult?: ValidationResult;
    validationLoading?: boolean;
    loading?: boolean;
    firstLoadComplete?: boolean;
  }>();

  config = AppConfig.settings;

  dailyLogDate: PtLuxonDate;
  gridData: JackingFormInstanceSummary[] = [];
  gridLoading: boolean = false;

  columns = [
    { field: 'formTypeName', title: 'Activity' },
    { field: 'startTime', title: 'Start', filter: 'time' },
    { field: 'endTime', title: 'End', filter: 'time' },
    { field: 'lastUpdated', title: 'Last Updated', filter: 'time' },
    { field: 'lastUpdatedBy', title: 'Last Updated By' },
    { field: 'activityStatusName', title: 'Status' },
    { field: 'actions', title: 'Actions', width: 120 },
  ];

  fieldPositioningGridData: GridDataResult;
  fieldPositioningData: FieldPositioningEditor[] = [];
  fieldPositioningState: State;
  fieldPositioningColumns = fieldPositioningColumns;
  fieldPositioningGridLoading = false;

  onSiteMovementGridData: GridDataResult;
  onSiteMovementData: OnSiteMovementEditor[] = [];
  onSiteMovementState: State;
  onSiteMovementColumns = onSiteMovementColumns;
  onSiteMovementGridLoading = false;

  // Permissions/day state (you can start minimal)
  permissions: JackingPermissions = new JackingPermissions();
  dayCompleted = false;
  isImplementerActiveForDay = true;

  // UI bits used by the grid classes
  sidebarCollapsed = false;

  public readonly offsetFieldByTimeField: Record<string, string> = {
    startTime: 'startTimeOffset',
    endTime: 'endTimeOffset',
    lastUpdated: 'lastUpdatedOffset',
  };
  private changeSubscription: Subscription;
  private notificationReloadTimer: number;
  private notificationReloadInFlight = false;
  private notificationReloadPending = false;

  constructor(
    private navCtrl: NavController,
    private jackingV2Service: JackingV2Service,
    private completeDayService: CompleteDayService,
    private fieldPositioningService: FieldPositioningService,
    private permissionsService: PermissionsService,
    private jackingActivityReportService: JackingActivityReportService,
    private onSiteMovementService: OnSiteMovementService,
    private jackingV2FileDownloadService: JackingV2FileDownloadService,
    private validationService: ValidationService,
    private implementerService: ImplementerService,
    private signalRService: SignalRService,
    private vesselContextService: VesselContextService,
  ) {}


  async loadForDate(date: PtLuxonDate) {
    this.dailyLogDate = date;
    this.subscribeToChanges();
    this.signalRService.connect(this.vesselContextService.getVesselId())
      .catch(error => console.error('Unable to connect to Jacking change notifications', error));
    await this.loadData();
  }

  ngOnDestroy(): void {
    if (this.changeSubscription) {
      this.changeSubscription.unsubscribe();
      this.changeSubscription = null;
    }
    if (this.notificationReloadTimer) {
      window.clearTimeout(this.notificationReloadTimer);
    }
  }

  // router forwards picker events to these
  async dayUnlockedForEditing() {
    await Promise.all([
      this.checkDayCompletedForDate(this.dailyLogDate).then(v => this.dayCompleted = v),
      this.checkImplementerActive(),
    ]);
  }

  dateBeforeInitialSetupChanged(_: boolean) {
  }

  async checkDayCompletedForDate(date: PtLuxonDate): Promise<boolean> {
    if (!date?.dateTime) {
      return false;
    }

    const filters = new CompleteDayFilters();
    filters.date = date.dateTime;

    return this.completeDayService.checkDay(filters);
  }

  private async loadData() {
    this.loading = true;

    this.fieldPositioningGridLoading = true;
    this.onSiteMovementGridLoading = true;
    this.gridLoading = true;

    this.gridData = [];
    this.fieldPositioningGridData = null;
    this.onSiteMovementGridData = null;

    this.uiChange.emit({
      loading: true,
      validationLoading: false,
      validationResult: new ValidationResult(),
    });

    try {
      const unixMs = this.dailyLogDate.dateTime.toMillis();

      const [summaries, fieldPositionings, onSiteMovements, permissions, isDayCompleted, _] = await Promise.all([
        this.jackingV2Service.getSummariesForDate(unixMs),
        this.fieldPositioningService.getActivitiesForDateFromApi(unixMs),
        this.onSiteMovementService.getOnSiteMovementsForDateFromApi(unixMs),
        this.permissionsService.jackingPermissions(),
        this.checkDayCompletedForDate(this.dailyLogDate),
        this.checkImplementerActive(),
      ]);

      // Jacking list
      this.gridData = this.mapSummaries(summaries);

      // Field positioning
      this.fieldPositioningData = fieldPositionings || [];
      if (!this.fieldPositioningState) {
        this.fieldPositioningState = this.fieldPositioningService.getState();
      }
      this.fieldPositioningGridData = process(this.fieldPositioningData, this.fieldPositioningState);
      this.fieldPositioningGridLoading = false;

      // osm
      this.onSiteMovementData = onSiteMovements || [];
      if (!this.onSiteMovementState) {
        this.onSiteMovementState = this.onSiteMovementService.getState();
      }
      this.onSiteMovementGridData = process(this.onSiteMovementData, this.onSiteMovementState);
      this.onSiteMovementGridLoading = false;

      this.permissions = permissions;
      this.dayCompleted = isDayCompleted;
    } catch (error) {
      console.error('Error loading V2 jacking data', error);
    } finally {
      this.validateJackingPage();
      this.loading = false;
      this.gridLoading = false;
      this.fieldPositioningGridLoading = false;
      this.onSiteMovementGridLoading = false;
      this.uiChange.emit({ loading: false });
      if (this.notificationReloadPending) {
        this.scheduleNotificationReload();
      }
    }
  }

  private subscribeToChanges(): void {
    if (this.changeSubscription && !this.changeSubscription.closed) {
      return;
    }
    this.changeSubscription = this.signalRService.changes$.subscribe(notification => {
      if (this.isRelevantNotification(notification)) {
        this.scheduleNotificationReload();
      }
    });
  }

  private isRelevantNotification(notification: DataChangeNotification): boolean {
    return !!notification &&
      Number(notification.vesselId) === Number(this.vesselContextService.getVesselId()) &&
      notification.area === 'JackingV2' &&
      notification.entityType === 'JackingFormInstance' &&
      notification.clientInstanceId !== this.signalRService.clientInstanceId;
  }

  private scheduleNotificationReload(): void {
    this.notificationReloadPending = true;
    if (this.notificationReloadInFlight || this.gridLoading) {
      return;
    }
    if (this.notificationReloadTimer) {
      window.clearTimeout(this.notificationReloadTimer);
    }
    this.notificationReloadTimer = window.setTimeout(() => {
      this.notificationReloadTimer = null;
      this.reloadJackingSummaries().catch(error => console.error('Unable to refresh Jacking summaries', error));
    }, 250);
  }

  private async reloadJackingSummaries(): Promise<void> {
    if (!this.dailyLogDate?.dateTime || this.notificationReloadInFlight) {
      return;
    }

    this.notificationReloadPending = false;
    this.notificationReloadInFlight = true;
    this.gridLoading = true;
    const requestedDate = this.dailyLogDate.dateTime.toMillis();
    try {
      const summaries = await this.jackingV2Service.getSummariesForDate(requestedDate);
      if (this.dailyLogDate?.dateTime?.toMillis() !== requestedDate) {
        return;
      }
      this.gridData = this.mapSummaries(summaries);
      await this.validateJackingPage();
    } finally {
      this.notificationReloadInFlight = false;
      this.gridLoading = false;
      if (this.notificationReloadPending) {
        this.scheduleNotificationReload();
      }
    }
  }

  private mapSummaries(summaries: JackingFormInstanceSummary[]): JackingFormInstanceSummary[] {
    return (summaries || []).map(s => ({
      ...s,
      startTime: (s as any).startTime,
      endTime: (s as any).cancellationTime || (s as any).endTime,
      endTimeOffset: (s as any).cancellationTime ? (s as any).cancellationTimeOffset : (s as any).endTimeOffset
    }));
  }

  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  async validateJackingPage() {
    this.validationService.validateFieldPositioning(this.dailyLogDate, this.validationResult).then(fieldPositioningResult => {
      const jackingResult = this.jackingV2Service.validateFormInstances(fieldPositioningResult, this.gridData);
        this.validationService.validateOnSiteMovement(this.dailyLogDate, jackingResult, null).then(result => {
          this.validationResult = result;
          this.validationLoading = false;
        });
      });
  }

  formatLuxon(dt: any, offsetHours?: number | null): string {
    if (!dt) { return ''; }

    // parse as UTC then apply offset hours
    const utc = DateTime.fromISO(dt, { zone: 'utc' });
    if (!utc.isValid) { return ''; }

    const off = offsetHours ?? 0;
    const local = utc.plus({ hours: off });

    const timePart = local.toFormat('dd/MM/yyyy HH:mm');

    // only append offset when actually supplied
    if (offsetHours === null || offsetHours === undefined) {
      return timePart;
    }

    const sign = off >= 0 ? '+' : '-';
    return `${timePart} (${sign}${Math.abs(off)})`;
  }

  editJacking(row: any) {
    this.navCtrl.push(JackingActivityV2EditPage, {
      jackingInstanceId: row.id,
      currentDate: this.dailyLogDate
    }, { animation: 'ios-transition' });
  }

  editFieldPositioning(row: FieldPositioningEditor) {
    this.navCtrl.push(FieldPositioningEditPage, {
      activity: row,
      dailyLogDate: this.dailyLogDate,
    }, { animation: 'ios-transition' });
  }

  downloadFieldPositioning(row: FieldPositioningEditor & any, isPortrait: boolean) {
    this.patchFieldPositioningRow(row.id, isPortrait
      ? { portraitLoading: true }
      : { landscapeLoading: true }
    );

    const downloadLink = this.fieldPositioningService.generateDprDownloadLink(
      this.dailyLogDate,
      row.id,
      isPortrait,
      this.config.isImplementerEnvironment,
    );

    this.jackingActivityReportService.generateFormBlob(downloadLink)
      .pipe(finalize(() => {
        // always clear both
        this.patchFieldPositioningRow(row.id, { portraitLoading: false, landscapeLoading: false });
      }))
      .subscribe(
        (resp: HttpResponse<Blob>) => {
          const contentDisposition = resp.headers.get('content-disposition');
          const file = resp.body;
          const fileURL = URL.createObjectURL(file);

          const searchTerm = 'filename=';
          let filename = contentDisposition.slice(contentDisposition.indexOf(searchTerm) + searchTerm.length);
          if (filename.slice(0, 1) === '"') {
            filename = filename.substring(1, filename.length - 1);
          }

          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          link.click();
        },
        () => {
          // swallow if you want; finalize still runs
        }
      );
  }

  private patchFieldPositioningRow(rowId: number, patch: Partial<FieldPositioningEditor & any>) {
    this.fieldPositioningData = (this.fieldPositioningData || []).map(r =>
      r.id === rowId ? ({ ...r, ...patch }) : r
    );

    if (this.fieldPositioningState) {
      this.fieldPositioningGridData = process(this.fieldPositioningData, this.fieldPositioningState);
    }
  }

  public async editOnSiteMovementRecord(editor: OnSiteMovementEditor, readOnly: boolean) {
    if (!editor) { return; }

    await this.navCtrl.push(OnSiteMovementEditPage, {
      onSiteMovementRecord: editor,
      dailyLogDate: this.dailyLogDate,
      readOnly,
    }, { animation: 'ios-transition' });
  }

  public downloadJackingForm(row: JackingFormInstanceSummary, incrementVersion: boolean) {
    this.setRowLoadingIcon(row, incrementVersion);

    const formTypeId = row.formTypeName as ('Arrival' | 'Departure');

    this.jackingV2Service
      .downloadJackingPdf(this.dailyLogDate, row.id, row.formTypeId as number, incrementVersion, false)
      .pipe(finalize(() => this.clearRowLoading(row)))
      .subscribe({
        next: (resp) => {
          const fallback = `${formTypeId}-Report.pdf`;
          this.jackingV2FileDownloadService.downloadHttpBlobResponse(resp, fallback);
        }
      });
  }

  public canGenerateReport(row: JackingFormInstanceSummary): boolean {
    return !!row && this.jackingV2Service.isFinalisedFormInstanceStatus(row);
  }

  private setRowLoadingIcon(row: JackingFormInstanceSummary, incrementVersion: boolean) {
    if (incrementVersion) {
      row.generateFormLoading = true;
    } else {
      row.viewFormLoading = true;
    }
  }

  public clearRowLoading(row: any) {
    row.viewFormLoading = false;
    row.generateFormLoading = false;
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/jacking-activity-list-v2/jacking-activity-list-v2.component.ts